import { motion } from "framer-motion";
import {
  Radar,
  BrainCircuit,
  Kanban,
  Sparkles,
  ShieldCheck,
  CalendarClock,
  History,
  Layers,
  KeyRound,
  Globe2,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";
import { Container, Chapter, FadeUp } from "./primitives";

const FEATURES = [
  {
    id: "discovery",
    icon: Radar,
    title: "AI Lead Discovery",
    body: "Continuously scan public conversations across platforms and surface prospects who are actively signaling.",
    span: "lg:col-span-2 lg:row-span-2",
    highlight: true,
  },
  { id: "intel", icon: BrainCircuit, title: "Lead Intelligence", body: "AI scores every profile based on buying intent, seniority and fit." },
  { id: "crm", icon: Kanban, title: "CRM Pipeline", body: "Manage every opportunity — from discovery to conversion — on one board." },
  { id: "drafts", icon: Sparkles, title: "Smart Drafts", body: "Generate contextual, human-sounding replies grounded in the thread." },
  { id: "approvals", icon: ShieldCheck, title: "Approval Workflow", body: "Review every outgoing engagement before it goes live. Nothing slips." },
  { id: "sched", icon: CalendarClock, title: "Scheduler", body: "Plan interactions at the perfect time, in the right time zone." },
  { id: "audit", icon: History, title: "Audit Logs", body: "Every action tracked. Compliance ready by default." },
  { id: "multi", icon: Layers, title: "Multi-Account", body: "Manage multiple social accounts securely under one workspace." },
  { id: "oauth", icon: KeyRound, title: "OAuth Auth", body: "Official API integrations wherever available. No brittle scraping." },
  { id: "browser", icon: Globe2, title: "Browser Automation", body: "Secure browser-based workflows for supported actions." },
  { id: "analytics", icon: BarChart3, title: "Analytics", body: "Measure campaign performance and team productivity in real time." },
];

export default function Solution() {
  return (
    <section id="solution" data-testid="solution-section" className="relative py-32 md:py-40 border-t border-white/10">
      <div className="orb bg-[#4F46E5] w-[520px] h-[520px] -bottom-40 -right-32 opacity-25" />
      <Container className="relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <Chapter
            number="02"
            kicker="The Solution"
            title={
              <>
                Everything your social selling{" "}
                <span className="font-serif-x italic">team</span> needs.
              </>
            }
            description="One platform to discover, qualify, engage, approve, schedule and analyze — replacing five tools and countless hours."
          />
          <FadeUp delay={0.2}>
            <a
              href="#pricing"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2.5 text-sm font-medium hover:bg-white/[0.06] transition-colors"
            >
              See it in action
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <FadeUp key={f.id} delay={i * 0.04}>
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-7 md:p-8 h-full min-h-[220px] flex flex-col hover:border-white/25 transition-colors ${f.span || ""} ${
                  f.highlight ? "bg-gradient-to-br from-[#4F46E5]/15 via-[#0F172A]/60 to-transparent" : ""
                }`}
              >
                {f.highlight && (
                  <>
                    <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#7C3AED]/30 blur-3xl" />
                    <div className="pointer-events-none absolute inset-0 opacity-40 [mask-image:radial-gradient(circle_at_top_right,black,transparent_60%)] grid-bg" />
                  </>
                )}

                <div className="relative flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                    <f.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/30">
                    Feature/{String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h4 className={`relative mt-auto pt-16 font-display tracking-[-0.02em] leading-tight text-white ${f.highlight ? "text-3xl md:text-5xl" : "text-xl md:text-[22px]"}`}>
                  {f.title}
                </h4>
                <p className={`relative mt-3 text-white/60 leading-relaxed ${f.highlight ? "text-base md:text-lg max-w-md" : "text-sm md:text-[15px]"}`}>
                  {f.body}
                </p>

                {f.highlight && (
                  <div className="relative mt-6 inline-flex items-center gap-2 text-sm text-[#a78bfa]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#a78bfa] animate-pulse" />
                    <span className="font-mono-x tracking-widest text-xs uppercase">Scanning 12.4M threads / mo</span>
                  </div>
                )}
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </section>
  );
}
